import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import {
  DebounceTimer,
  LoadingStatus,
  ToastThemes,
} from '../../../constants/common';
import ListView from '../../../atoms/DataList/ListView';
import { allClaimsHeadCell } from '../claims-utlis/claims-utils';
import {
  currentDate,
  getFormattedValues,
  getSGTTimeZone,
  noOfmonthfromCurrent,
} from '../../../utils/formatter';
import {
  InfoOutlined,
  SettingsBackupRestoreOutlined,
} from '@mui/icons-material';
import CircularLoader from '../../../atoms/CircularLoader/circular-loader';
import TryAgain from '../../molecules/ErrorState/error.component';
import { formatCurrency, showToast } from '../../../utils/common.util';
import { ViewAccess } from '../../OnBoarding/Organization/RolesAndPermissions/constants/roles-permissions.constants';
import { DateRangePro } from '../../molecules/DateRange/DateRangePro';
import { NewClaimsService } from '../../../services/newClaims';
import { CLAIM_STATUS } from '../../claim-view/claim-audit-log';
import ApiSelectTag from '../../../atoms/Select/ApiSelectTag';
import ApproveRejectModalAllClaims from './all-claims-approve-reject-modal';

const AllClaimsAllDasboard = (props) => {
  const { currentUserRoleId } = useSelector((state) => state.main);
  const [checkedItems, setCheckedItems] = useState([]);
  const { claimsPermission } = useSelector((state) => state.modulePermissions);
  const { logoImage } = useSelector((state) => state && state.onBoarding);
  const defaultMonths = logoImage?.defaultMonths;
  const defaultTimezone = logoImage?.defaultTimezone;
  const [loading, setLoading] = useState(-1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState({ });
  const [status, setStatus] = useState(searchParams?.get('status') || CLAIM_STATUS.PENDING);
  const [matchedPermissions, setMatchedPermissions] = useState();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState([
    noOfmonthfromCurrent(defaultMonths || 3),
    currentDate,
  ]);
  const searchQuery = searchParams?.get('q');
  const debouncedVal = useDebounce(searchQuery, DebounceTimer);

  function getMatchedApprovalPermissions(checkedItems, approvalPermission) {
    const moduleIds = new Set(checkedItems.map((item) => item.moduleId));
    return approvalPermission?.filter((perm) =>
      moduleIds.has(perm.fkAwfFormId.id)
    );
  }

  useEffect(() => {
    const matchedPermissions = getMatchedApprovalPermissions(checkedItems, list?.approvalRemarksPermission);
    setMatchedPermissions(matchedPermissions);
  }, [checkedItems]);

  useEffect(async () => {
    if (currentUserRoleId && status) {
      setLoading(LoadingStatus.Loading);
      await new NewClaimsService()
        .getAllClaimsList(
          currentUserRoleId,
          searchQuery,
          getFormattedValues('YYYY-MM-DD', selectedDate[0]),
          getFormattedValues('YYYY-MM-DD', selectedDate[1]),
          status === 'ALL' ? null : status
        )
        .then((res) => {
          setList(res);
          setLoading(LoadingStatus.Success);
        })
        .catch((err) => {
          showToast(err, ToastThemes.error);
          setLoading(LoadingStatus.Failure);
        });
    }
  }, [currentUserRoleId, debouncedVal, selectedDate, status]);

  useEffect(() => {
    setCheckedItems([]);
  }, [list?.claimList]);

  const permissionById = Object.values(claimsPermission).reduce((acc, curr) => {
    acc[curr.id] = curr;
    return acc;
  }, {});

  const handleAddClick = () => {
    navigate('create-claim');
  };

  const rowData = list?.claimList?.map((items) => {
    const {
      id,
      referenceNumber,
      claimType,
      createdAt,
      amount,
      status,
      claimantName,
      attachments,
      departmentName,
      userBasedDropdown,
      distance,
      computedAmount,
      paymentDate,
      subtypeName,
      claimTypeId,
      moduleId,
    } = items;
    return {
      referenceNumber: referenceNumber,
      name:
        claimsPermission?.['all-claims']?.viewAccess !== ViewAccess.MY_ACCESS
          ? claimantName
          : { hide: true },
      dept: departmentName,
      type: claimsPermission?.['all-claims']?.showSubtype
        ? subtypeName
        : claimType,
      createdAt: getFormattedValues('YYYY-MM-DD', createdAt),
      amount: formatCurrency(Number(amount)),
      status:
        claimsPermission?.['all-claims']?.viewAccess === ViewAccess.MY_ACCESS &&
        status === 'SENT BACK'
          ? 'RE-SUBMIT'
          : status,
      action: (
        <IconButton
          onClick={() => navigate(`/new-claims/${claimTypeId}/${id}?all=Y`)}
        >
          <InfoOutlined sx={{ color: 'primary.main80' }} />
        </IconButton>
      ),
      id: {
        value: {
          id,
          referenceNumber,
          status,
          attachments,
          amount,
          claimantName,
          userBasedDropdown,
          distance,
          computedAmount,
          claimTypeId,
          moduleId,
        },
        hide: true,
      },
    };
  });

  const isDisabledReset = () => {
    return (
      selectedDate[0].isSame(noOfmonthfromCurrent(defaultMonths || 3)) &&
      selectedDate[1].isSame(currentDate)
    );
  };

  function getApprovalFlags(matchedPermissions) {
    return {
      isApprovalRemarksRequired: matchedPermissions?.some(p => p?.isApprovalRemarksRequired === true),
      isSendBackRemarksRequired: matchedPermissions?.some(p => p?.isSendBackRemarksRequired === true),
    };
  }

  const resetFilters = () => {
    setSelectedDate([noOfmonthfromCurrent(defaultMonths || 3), currentDate]);
  };

  const handleDropDown = (event) => {
    const { value, name } = event.target;
    setStatus(value?.id);
  };

  const isDisabledCheckbox = (item) => {
    const viewAccess = permissionById[item?.id?.value?.moduleId]?.viewAccess;
    return viewAccess !== ViewAccess.APPROVER_APPROVE_ACCESS;
  };

  if (loading === LoadingStatus.Failure) {
    return <TryAgain />;
  }

  return (
    <>
      <CircularLoader show={loading === LoadingStatus.Loading} />
      <Box sx={{ backgroundColor: 'white' }}>
        <Box display="flex" justifyContent="space-between">
          <Box mt={0.5} mb={1} p={2}>
            <ApiSelectTag
              defaultValue={'Select Status'}
              fullWidth
              size="small"
              name="benefitId"
              value={status}
              onchange={handleDropDown}
              color={true}
              dropDownList={[
                { id: 'ALL', name: 'ALL' },
                { id: CLAIM_STATUS.PENDING, name: 'PENDING' },
                {
                  id: CLAIM_STATUS.RE_SUBMIT,
                  name:
                    claimsPermission?.['all-claims']?.viewAccess ===
                    ViewAccess.MY_ACCESS
                      ? 'RE-SUBMIT'
                      : 'SENT BACK',
                },
                { id: CLAIM_STATUS.APPROVED, name: 'APPROVED' },
                { id: CLAIM_STATUS.REJECTED, name: 'REJECTED' },
                { id: CLAIM_STATUS.REVERSE, name: 'REVERSED' },
              ]}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={2} mb={1} p={2}>
            <DateRangePro
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              greenText={true}
            />
            <Button
              startIcon={<SettingsBackupRestoreOutlined />}
              sx={{ textTransform: 'capitalize', mt: -1.5 }}
              disabled={isDisabledReset()}
              onClick={resetFilters}
            >
              Clear
            </Button>
            {(claimsPermission?.['all-claims']?.viewAccess ===
              ViewAccess.APPROVER_APPROVE_ACCESS ||
              claimsPermission?.['all-claims']?.viewAccess ===
                ViewAccess.ALL_APPROVED_ACCESS) && (
              <Box pr={1}>
                <ApproveRejectModalAllClaims
                  checkedItems={checkedItems}
                  currentUserRoleId={currentUserRoleId}
                  viewAccess={claimsPermission?.['all-claims']?.viewAccess}
                  searchQuery={searchQuery}
                  selectedDate={selectedDate}
                  status={status}
                  approvalRemarksPermissions={getApprovalFlags(matchedPermissions)}
                  setData={setList}
                />
              </Box>
            )}
          </Box>
        </Box>
        <ListView
          headCells={allClaimsHeadCell({
            my_access:
              claimsPermission?.['all-claims']?.viewAccess !==
              ViewAccess.MY_ACCESS,
            approver_access:
              claimsPermission?.['all-claims']?.viewAccess !==
              ViewAccess.APPROVER_APPROVE_ACCESS,
          })}
          rowsData={rowData || []}
          isDisabledCheckbox={(item) => isDisabledCheckbox(item)}
          showSerialNo={true}
          showCheckbox={true}
          setCheckedItems={setCheckedItems}
          checkedItems={checkedItems}
          allowSelectAll={false}
          onAddClick={handleAddClick}
          showAddIcon={false}
        />
      </Box>
    </>
  );
};

export default AllClaimsAllDasboard;
